import * as React from 'react';

import { useAuth } from '@/components/auth/auth-provider';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import type { NavItem, NavSection } from '@/lib/navigation';

/** A menu entry the signed-in person pinned above Home. */
export type NavigationFavourite = { path: string; label: string };

export const MAX_NAVIGATION_FAVOURITES = 20;
export const MAX_FAVOURITE_LABEL_LENGTH = 60;
const STORAGE_PREFIX = 'cdf-nav-favourites:v1';
const CHANGE_EVENT = 'cdf-nav-favourites-change';

/** Favourites are personal to the signed-in account and to the selected tenant entity. */
export function navigationFavouritesStorageKey(accountId: string | undefined, tenantId: string, entityId: string) {
  return `${STORAGE_PREFIX}:${JSON.stringify([accountId ?? 'local', tenantId, entityId])}`;
}

function isInternalPath(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && value.length <= 500;
}

/** Validate stored data; anything malformed or duplicated is dropped rather than trusted. */
export function parseNavigationFavourites(raw: string | null): NavigationFavourite[] {
  if (!raw) return [];
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return []; }
  if (!Array.isArray(parsed)) return [];
  const seen = new Set<string>();
  const favourites: NavigationFavourite[] = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== 'object') continue;
    const { path, label } = entry as Record<string, unknown>;
    if (!isInternalPath(path) || seen.has(path) || typeof label !== 'string') continue;
    const trimmed = label.trim().slice(0, MAX_FAVOURITE_LABEL_LENGTH);
    if (!trimmed) continue;
    seen.add(path);
    favourites.push({ path, label: trimmed });
    if (favourites.length === MAX_NAVIGATION_FAVOURITES) break;
  }
  return favourites;
}

// Keeps favourites working for the session when browser storage is blocked or full.
const memory = new Map<string, string>();

function readRaw(key: string): string | null {
  if (memory.has(key)) return memory.get(key) ?? null;
  try { return localStorage.getItem(key); } catch { return null; }
}

function writeFavourites(key: string, favourites: NavigationFavourite[]) {
  const raw = JSON.stringify(favourites);
  memory.set(key, raw);
  try {
    localStorage.setItem(key, raw);
    memory.delete(key);
  } catch {
    // The in-memory copy keeps the current session consistent.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

function subscribe(onChange: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (event.key?.startsWith(STORAGE_PREFIX)) { memory.delete(event.key); onChange(); }
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

export type NavigationFavouritesState = {
  /** False until a tenant entity is selected; pinning is unavailable then. */
  available: boolean;
  items: NavigationFavourite[];
  isFull: boolean;
  isPinned: (path: string) => boolean;
  pin: (favourite: NavigationFavourite) => void;
  unpin: (path: string) => void;
  rename: (path: string, label: string) => void;
  /** Swap the positions of two favourites (used for move up / move down). */
  swap: (path: string, otherPath: string) => void;
  /** Move a favourite into another favourite's position (used for drag and drop). */
  move: (path: string, targetPath: string) => void;
};

/** Return a copy with `path` moved to the position currently held by `targetPath`. */
export function moveFavourite(
  favourites: NavigationFavourite[],
  path: string,
  targetPath: string
): NavigationFavourite[] {
  const from = favourites.findIndex(item => item.path === path);
  const to = favourites.findIndex(item => item.path === targetPath);
  if (from < 0 || to < 0 || from === to) return favourites;
  const next = [...favourites];
  const [moved] = next.splice(from, 1);
  if (!moved) return favourites;
  next.splice(to, 0, moved);
  return next;
}

/** Create, list, rename, reorder and remove the signed-in person's pinned menu entries. */
export function useNavigationFavourites(): NavigationFavouritesState {
  const { account } = useAuth();
  const { selected } = useTenantEntity();
  const key = selected ? navigationFavouritesStorageKey(account?.stableId, selected.tenantId, selected.entityId) : null;
  const raw = React.useSyncExternalStore(subscribe, () => (key ? readRaw(key) : null), () => null);

  return React.useMemo(() => {
    const items = parseNavigationFavourites(raw);
    const update = (change: (current: NavigationFavourite[]) => NavigationFavourite[]) => {
      if (!key) return;
      writeFavourites(key, change(parseNavigationFavourites(readRaw(key))));
    };
    return {
      available: Boolean(key),
      items,
      isFull: items.length >= MAX_NAVIGATION_FAVOURITES,
      isPinned: path => items.some(item => item.path === path),
      pin: ({ path, label }) => update(current => {
        const trimmed = label.trim().slice(0, MAX_FAVOURITE_LABEL_LENGTH);
        if (!isInternalPath(path) || !trimmed || current.some(item => item.path === path)
          || current.length >= MAX_NAVIGATION_FAVOURITES) return current;
        return [...current, { path, label: trimmed }];
      }),
      unpin: path => update(current => current.filter(item => item.path !== path)),
      rename: (path, label) => update(current => {
        const trimmed = label.trim().slice(0, MAX_FAVOURITE_LABEL_LENGTH);
        return trimmed ? current.map(item => (item.path === path ? { ...item, label: trimmed } : item)) : current;
      }),
      swap: (path, otherPath) => update(current => {
        const first = current.find(item => item.path === path);
        const second = current.find(item => item.path === otherPath);
        if (!first || !second || first === second) return current;
        return current.map(item => (item === first ? second : item === second ? first : item));
      }),
      move: (path, targetPath) => update(current => moveFavourite(current, path, targetPath))
    };
  }, [key, raw]);
}

/** Whether a menu entry is an in-app screen that can be pinned. */
export function isPinnableNavItem(item: Pick<NavItem, 'to' | 'external' | 'dynamic'>): item is NavItem & { to: string } {
  return Boolean(item.to && isInternalPath(item.to) && !item.external && !item.dynamic);
}

/**
 * Turn stored favourites into menu entries using the person's current menu.
 * Favourites for screens they can no longer see (permissions, modules, layout) stay hidden.
 */
export function resolveFavouriteItems(sections: NavSection[], favourites: NavigationFavourite[]): NavItem[] {
  const targets = new Map<string, Pick<NavItem, 'icon' | 'moduleKey'>>();
  for (const section of sections) {
    for (const item of section.items) {
      const moduleKey = item.moduleKey ?? section.moduleKey;
      if (isPinnableNavItem(item) && !targets.has(item.to)) targets.set(item.to, { icon: item.icon, moduleKey });
      for (const child of item.children ?? []) {
        if (!targets.has(child.to)) targets.set(child.to, { icon: item.icon, moduleKey });
      }
    }
  }
  return favourites.flatMap(favourite => {
    const target = targets.get(favourite.path);
    return target ? [{ title: favourite.label, to: favourite.path, ...target }] : [];
  });
}
